/**
 * Patient profile photo — Vuex module
 *
 * Backs the round avatar in the Patient Profile header: tap it, take/choose a
 * photo, and it saves immediately (no in-app confirm step — the phone's own
 * camera UI already has one, and a second confirm is what makes staff give up
 * halfway through).
 *
 * THREE-HOP UPLOAD, same shape as Tx Presenter photo attachments
 * (txPresenter.js::uploadPatientPhoto):
 *   1. compress/transcode in the browser
 *   2. uploadToS3() → presigned POST straight to S3 (nothing large through Lambda)
 *   3. /save-patient-photo with the KEY — the backend records it and queues the
 *      copy into Open Dental's imaging category.
 *
 * WHY THE FORCED JPEG TRANSCODE
 * /get-patient-photo-upload-url refuses anything but image/jpeg, and Open Dental's
 * imaging tables want a JPEG too. A phone photo already is one; a PNG screenshot
 * or a WebP is not. So we ask compressImage for an explicit `fileType` AND pass
 * `minSizeForCompression: 0` — without that floor override the shared helper
 * short-circuits on any file under 500KB and hands back the ORIGINAL, still-PNG
 * file, which S3 then rejects with a bare 403. Transcoding is not compression's
 * side effect here; it is the point.
 *
 * The File is rebuilt with a fixed 'profile-photo.jpg' name because
 * browser-image-compression keeps the input's name (and therefore a '.png'
 * extension on a JPEG blob), and uploadToS3 derives the S3 key from that name.
 *
 * Deliberately STATELESS. There is exactly one photo per patient and exactly one
 * component showing it; caching it in Vuex would only add a second copy to keep
 * in sync with the 1-hour presigned URL the backend hands back.
 */

import axios from 'axios';
import { uploadToS3 } from '@/utils/fileUpload';
import { compressImage } from '@/utils/imageCompression';

// Max edge length of the stored photo. A header avatar renders at 48–72px and the
// viewer at a phone width, so 800px is already generous at 2x — bigger only costs
// the practice upload time on office wifi.
const MAX_EDGE_PX = 800;

// "Photo to OD" sends a DOCUMENT (insurance card, medication list). It has to stay
// readable in Open Dental, so it keeps far more pixels than a face in a 72px circle.
// The server accepts up to 5MB; ~1MB at 2000px reads cleanly and uploads quickly.
const OD_IMAGE_MAX_EDGE_PX = 2000;

/**
 * Downscale + transcode to a JPEG File named `name`. Shared by both uploads, so the
 * JPEG-only contract (see the file header) is enforced in exactly one place.
 */
async function toJpeg(file, { maxEdge, maxSizeMB, initialQuality, name }) {
  const result = await compressImage(file, {
    maxWidthOrHeight: maxEdge,
    maxSizeMB,
    initialQuality,
    fileType: 'image/jpeg',
    minSizeForCompression: 0,
  });

  // compressImage NEVER throws — it returns the original file with
  // `skipped: true` when the type is not compressible (HEIC, GIF, SVG) or the
  // library failed. Both cases leave us with a non-JPEG the upload endpoint
  // would reject, so turn it into a sentence a front-desk person can act on.
  if (!result.file || result.file.type !== 'image/jpeg') {
    throw new Error('That image type is not supported. Please use a JPEG or PNG photo.');
  }

  return new File([result.file], name, { type: 'image/jpeg' });
}

export default {
  namespaced: true,

  state: {},

  actions: {
    /**
     * Current photo for a patient. Returns { url, updatedAt, enabled } — `url` is
     * null when there is none, and the endpoint answers 200 with enabled:false
     * (never 403) when the site is not on the feature, so this is safe to call
     * unconditionally.
     */
    async load(_, { siteID, patNum } = {}) {
      if (!siteID || !patNum) throw new Error('siteID and patNum are required');
      const response = await axios.post('/get-patient-photo', { siteID, patNum }, { crossdomain: true });
      return response.data;
    },

    /**
     * Downscale → transcode to JPEG → S3 → record. Resolves with the save
     * endpoint's body: { url, updatedAt, odStatus, odReason?, category, categorySource }.
     */
    async upload(_, { siteID, patNum, file } = {}) {
      if (!siteID || !patNum) throw new Error('siteID and patNum are required');
      if (!file) throw new Error('file is required');

      const jpeg = await toJpeg(file, {
        maxEdge: MAX_EDGE_PX,
        maxSizeMB: 0.3,
        initialQuality: 0.82,
        name: 'profile-photo.jpg',
      });

      const uploaded = await uploadToS3({
        file: jpeg,
        siteID,
        patNum,
        teamioType: 'PATIENT_PHOTO',
        endpoint: '/get-patient-photo-upload-url',
        // We already compressed AND transcoded above. Leaving this on would run
        // the shared helper's own compressImageSimple() a second time, which
        // re-reads fileType from the file and would happily undo the transcode.
        compress: false,
      });

      const saved = await axios.post('/save-patient-photo', {
        siteID,
        patNum,
        key: uploaded.key,
      }, { crossdomain: true });

      return saved.data;
    },

    /**
     * Removes the Teamio copy only. Anything already queued/copied into Open
     * Dental stays there — OD is the practice's record of truth and we do not
     * reach into it to delete. A save still waiting for the exe is NOT cancelled:
     * it reads its own transit copy, which this never touches (2026-09-24).
     */
    async remove(_, { siteID, patNum } = {}) {
      if (!siteID || !patNum) throw new Error('siteID and patNum are required');
      const response = await axios.post('/delete-patient-photo', { siteID, patNum }, { crossdomain: true });
      return response.data;
    },

    /**
     * PHOTO TO OD — send one document photo into the Open Dental image category
     * staff picked. NOT the profile photo: its own endpoints, a fresh S3 key per
     * upload, and nothing kept in Teamio once OD has it.
     *
     * Resolves with { queued, category, categoryName, fileName }. Every failure
     * throws (including a queue failure — there is no "saved in Teamio anyway"
     * to fall back on), so the caller can keep the photo on screen to retry.
     */
    async uploadToOd(_, { siteID, patNum, file, categoryDefNum } = {}) {
      if (!siteID || !patNum) throw new Error('siteID and patNum are required');
      if (!file) throw new Error('file is required');
      if (categoryDefNum === undefined || categoryDefNum === null || categoryDefNum === '') {
        throw new Error('Pick an Open Dental category first.');
      }

      const jpeg = await toJpeg(file, {
        maxEdge: OD_IMAGE_MAX_EDGE_PX,
        maxSizeMB: 1,
        initialQuality: 0.85,
        name: 'photo.jpg',
      });

      const uploaded = await uploadToS3({
        file: jpeg,
        siteID,
        patNum,
        teamioType: 'PATIENT_OD_IMAGE',
        endpoint: '/get-patient-od-image-upload-url',
        // Already compressed and transcoded — a second pass would undo the transcode.
        compress: false,
      });

      const saved = await axios.post('/save-patient-od-image', {
        siteID,
        patNum,
        key: uploaded.key,
        categoryDefNum,
      }, { crossdomain: true });

      return saved.data;
    },

    /**
     * The site's visible Open Dental image categories, as [{ DefNum, ItemName }].
     *
     * Same endpoint as forms/getFormCategories, deliberately NOT that action: it
     * flips the page-wide alert/loading state and, on failure, SWALLOWS the error
     * into a global banner and resolves undefined — so a caller cannot tell "OD
     * unreachable" from "no categories". The Photo-to-OD sheet needs to show a
     * Retry for the first and a message for the second. This one throws.
     */
    async loadOdCategories(_, { siteID } = {}) {
      if (!siteID) throw new Error('siteID is required');
      const response = await axios.post('/get-form-categories', { siteID }, { crossdomain: true });
      const rows = response && response.data;
      if (!Array.isArray(rows)) throw new Error('Unexpected response while loading Open Dental categories');
      return rows
        .filter((r) => r && r.DefNum !== undefined && r.DefNum !== null)
        .map((r) => ({ DefNum: r.DefNum, ItemName: String(r.ItemName == null ? '' : r.ItemName).trim() }));
    },
  },
};
